import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useSiteData } from '../../context/DataContext';
import { FiShoppingCart, FiCheckCircle, FiArrowLeft, FiMessageCircle } from 'react-icons/fi';
import { useLoadingContext } from '../Loading/LoadingProvider';
import './Product.css';

function Product() {
  const { productId } = useParams();
  const { getProductById, getCategoryById } = useSiteData();
  const { startComponentLoading, stopComponentLoading } = useLoadingContext();
  const product = getProductById(productId);
  const [selectedSize, setSelectedSize] = useState(product?.sizes && product.sizes.length > 0 ? product.sizes[0].name : '');
  const [selectedColor, setSelectedColor] = useState(product?.colors && product.colors.length > 0 ? product.colors[0].name : 'black');
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const topRef = useRef(null);

  // Color options for posters
  const colorOptions = [
    { name: 'black', label: 'Classic Black', hex: '#1a1a1a' },
    { name: 'white', label: 'Pure White', hex: '#ffffff' },
    { name: 'darkWood', label: 'Natural Wood', hex: '#5D4037' }
  ];

  // Scroll to top when component mounts
  useEffect(() => {
    // Try multiple methods to ensure scrolling works
    const scrollToTop = () => {
      if (topRef.current) {
        topRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    };

    // Immediate scroll
    scrollToTop();

    // Delayed scroll to ensure it works after page load
    setTimeout(scrollToTop, 100);
    setTimeout(scrollToTop, 500);
  }, [productId, startComponentLoading, stopComponentLoading]);

  if (!product) return <div className="product-not-found">Product not found</div>;

  const category = getCategoryById(product.categoryId);

  const selectedSizeData = product.sizes && product.sizes.length > 0 ? product.sizes.find(s => s.name === selectedSize) : null;
  const currentPrice = selectedSizeData?.price || product.price;

  return (
    <div className="product-page">
      <div ref={topRef}></div>

      {/* Back to Shop Header */}
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        className="product-nav-header"
      >
        <button onClick={() => window.history.back()} className="back-link">
          <FiArrowLeft /> Back to Collection
        </button>
      </motion.div>

      <div className="product-container">
        {/* Left Section: Image Display */}
        <motion.div
          className="product-image-section"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8 }}
        >
          <div className="product-image-wrapper">
            <img src={product.image} alt={product.name} className="product-main-image" />
            <div className="image-overlay-glow"></div>
          </div>
        </motion.div>

        {/* Right Section: Product Details */}
        <div className="product-info-section">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <span className="product-category-tag">{category?.name}</span>
            <h1 className="product-title">{product.name}</h1>

            <div className="price-box">
              <div className="price-display">
                <span className="current-price">{currentPrice} EGP</span>
              </div>
            </div>

            <p className="product-description">
              {product.description || "A masterpiece of visual storytelling, printed on museum-quality canvas with archival inks. This piece is designed to transform any space into a portal of imagination and sophisticated style."}
            </p>

            <div className="product-options">
              <div className="option-group">
                <h4>Select Dimensions</h4>
                <div className="size-options">
                  {(product.sizes || []).map((size, idx) => (
                    <button
                      key={idx}
                      className={`size-option${selectedSize === size.name ? ' selected' : ''}`}
                      onClick={() => setSelectedSize(size.name)}
                    >
                      {size.dimensions || size.name}
                    </button>
                  ))}
                  {(!product.sizes || product.sizes.length === 0) && (
                    <button className="size-option selected">Standard Size</button>
                  )}
                </div>
              </div>

              <div className="option-group">
                <h4>COLORS</h4>
                <div className="frame-options">
                  {(product.colors && product.colors.length > 0 ? product.colors : colorOptions).map((color, idx) => (
                    <button
                      key={idx}
                      className={`frame-option${selectedColor === color.name ? ' selected' : ''}`}
                      onClick={() => setSelectedColor(color.name)}
                    >
                      <span className="color-dot" style={{ background: color.hex }}></span>
                      {color.name}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="purchase-actions">
              <button
                className="add-to-cart-premium-btn"
                onClick={async () => {
                  try {
                    setIsAddingToCart(true);
                    startComponentLoading('addToCart', 'Curating...');

                    const cartItem = {
                      id: `${product.id}-${selectedSize}-${selectedColor}`,
                      productId: product.id,
                      name: product.name,
                      category: category?.name,
                      size: selectedSize,
                      color: colorOptions.find(c => c.name === selectedColor)?.label,
                      price: currentPrice,
                      image: product.image,
                      quantity: 1
                    };

                    const existingCart = JSON.parse(localStorage.getItem('cart') || '[]');
                    const existingItemIndex = existingCart.findIndex(item => item.id === cartItem.id);

                    if (existingItemIndex >= 0) {
                      existingCart[existingItemIndex].quantity += 1;
                    } else {
                      existingCart.push(cartItem);
                    }

                    localStorage.setItem('cart', JSON.stringify(existingCart));
                    window.dispatchEvent(new CustomEvent('cartUpdated'));

                    await new Promise(resolve => setTimeout(resolve, 800));

                    const banner = document.createElement('div');
                    banner.className = 'premium-toast';
                    banner.innerHTML = `<span>Art added to your selection</span>`;
                    document.body.appendChild(banner);
                    setTimeout(() => banner.classList.add('visible'), 100);
                    setTimeout(() => {
                      banner.classList.remove('visible');
                      setTimeout(() => banner.remove(), 500);
                    }, 3000);

                  } catch (error) {
                    console.error('Error adding to cart:', error);
                  } finally {
                    setIsAddingToCart(false);
                    stopComponentLoading('addToCart');
                  }
                }}
                disabled={isAddingToCart || product.inStock === false}
                style={{ opacity: product.inStock === false ? 0.5 : 1, cursor: product.inStock === false ? 'not-allowed' : 'pointer' }}
              >
                {product.inStock === false ? 'OUT OF STOCK' : (
                    isAddingToCart ? 'CURATING...' : (
                      <>
                        <FiShoppingCart /> Add to Selection
                      </>
                    )
                )}
              </button>

              <button
                onClick={() => {
                  const items = [{
                    id: `${product.id}-${selectedSize}-${selectedColor}`,
                    productId: product.id,
                    name: product.name,
                    category: category?.name,
                    size: selectedSize,
                    color: colorOptions.find(c => c.name === selectedColor)?.label,
                    price: currentPrice,
                    image: product.image,
                    quantity: 1
                  }];
                  localStorage.setItem('cart', JSON.stringify(items));
                  window.dispatchEvent(new CustomEvent('cartUpdated'));
                  window.location.href = '/cart';
                }}
                className="checkout-now-btn"
                disabled={product.inStock === false}
                style={{ opacity: product.inStock === false ? 0.5 : 1, cursor: product.inStock === false ? 'not-allowed' : 'pointer' }}
              >
                <FiCheckCircle /> {product.inStock === false ? 'Unavailable' : 'Secure Checkout'}
              </button>
            </div>

            <div className="product-guarantees">
              <div className="guarantee-item">
                <span className="g-icon">✦</span>
                <span className="g-text">Premium Archival Grade</span>
              </div>
              <div className="guarantee-item">
                <span className="g-icon">✦</span>
                <span className="g-text">Hand-Stretched in Egypt</span>
              </div>
            </div>

            {/* Premium Customization CTA */}
            <motion.a
              href="https://wa.me/201112659808?text=Hi%20Wallz,%20I%20want%20to%20customize%20my%20own%20poster!"
              target="_blank"
              rel="noopener noreferrer"
              className="custom-order-card"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              whileHover={{ scale: 1.02 }}
            >
              <div className="custom-icon-wrapper">
                <FiMessageCircle />
              </div>
              <div className="custom-text">
                <p>Want something unique?</p>
                <span>Customize your own poster via WhatsApp</span>
              </div>
            </motion.a>
          </motion.div>
        </div>
      </div>
    </div>
  );
}

export default Product;
